import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { t } from 'i18next';
import React, { useState } from 'react';
import { client } from '../..';
import { SearchPeople } from '../../api/person';
import { createMillInvite, millInviteError } from '../../api/millInvite';
import { EnterLayout } from '../../components/EnterLayout';
import { checkEmail, showErrorMessage, showSuccessMessage } from '../../helper/helper';
import { Person } from '../../models/person/Person';
import { colorLight, colorPrimary } from '../../rsc/colors/colors';
import { Organisation } from '../../models/organisation/OrganisationModelV2';

type Props = {
    organisation: Organisation
    onSuccess: () => void
}

const UserInvitationDialog = ({ organisation, onSuccess }: Props) => {

    const [currentStep, setCurrentStep] = useState(1);
    const [showError, setShowError] = useState(false); // for step 1 error state
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const [personSearch, setPersonSearch] = useState<Person>(null)
    const [searchSuccess, isSearchSuccess] = useState<boolean>(undefined)


    const handleNext = () => {
        if (currentStep === 1) {
            startSearch();
        }
    };

    const startSearch = () => {
        // showError drives the field's error/helperText, which were already wired up.
        if (checkEmail(email) === null) {
            setShowError(true)
            return
        }
        setShowError(false)
        if (email != null && email.length > 0) {
            setIsLoading(true)
            client.query({
                query: SearchPeople,
                variables: {
                    text: email,
                    limit: 1
                }
            }).then(result => {
                const people = result.data.result?.people ?? []
                if (people.length > 0) {
                    const personFound = Person.build(people[0])
                    isSearchSuccess(true)
                    setPersonSearch(personFound)
                } else {
                    isSearchSuccess(false)
                    setPersonSearch(null)
                }
                setCurrentStep(2)
                // else {
                //     searchInFirebase()
                // }
            }).finally(() => {
                setIsLoading(false)
            })
        }
    }

    // const buildExistingPersonFromInput = () => {
    //     return {
    //         "id": -1, // set here to differenciate between existing person with mail found in our database vs mail not found in our database but found in firebase
    //         "firstname": "",
    //         "lastname": "",
    //         "user": {
    //             "email": email
    //         },
    //         "location": null
    //     }
    // }

    // const searchInFirebase = () => {
    //     firebaseAuth().fetchSignInMethodsForEmail(email)
    //         .then((providers) => {
    //             console.log(providers)
    //             if (providers.length > 0) {
    //                 setPersonSearch(buildPerson(buildExistingPersonFromInput()))
    //                 isSearchSuccess(true)
    //             } else {
    //                 isSearchSuccess(false)
    //             }
    //             setCurrentStep(2)
    //         })
    // }

    const handleClose = () => {
        console.log('closing')
        setIsOpen(false);
        // Reset state
        setTimeout(() => {
            setCurrentStep(1);
            setShowError(false);
            setEmail('');
            setFirstName('');
            setLastName('');
        }, 300);
    };

    const renderStepInit = () => (
        <EnterLayout onClick={handleNext}>

            <Box sx={{ py: 2 }}>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {t('invite_user_enter_email')}
                </Typography>

                <TextField
                    fullWidth
                    type="email"
                    label={t('email')}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setShowError(false) }}
                    error={showError}
                    helperText={showError ? t('invite_user_invalid_email') : ""}
                    variant="outlined"
                    sx={{ mb: 2 }}
                />

                <DialogActions sx={{ px: 0, pt: 3 }}>
                    <Button onClick={handleClose} variant="outlined" color="inherit">
                        {t('close')}
                    </Button>
                    <LoadingButton
                        sx={{ background: colorPrimary(), color: "#fff" }}
                        loading={isLoading}
                        onClick={handleNext}
                        disabled={!email.trim()}
                        variant="contained" >
                        {t('invite_user_next')}
                    </LoadingButton>
                </DialogActions>
            </Box>
        </EnterLayout>
    );

    // const personExistInOurDatabase = (person: Person): boolean => {
    //     return person?.transactor !== null && person?.transactor !== undefined
    // }

    // const personExistInFirebase = (person: Person): boolean => {
    //     return person.id === -1
    // }


    const invitePerson = async (person: Person | undefined) => {
        setIsLoading(true)
        try {
            await createMillInvite({
                email: person ? person.email : email,
                inviterOrganisationId: organisation.id,
                firstname: person ? person.firstname : firstName,
                lastname: person ? person.lastname : lastName,
                isPempemUser: !!person
            })
            showSuccessMessage(t('invitation_sent'))
            onSuccess()
            handleClose()
        } catch (e) {
            // Keep the dialog open on failure so the invite can be retried.
            showErrorMessage(millInviteError(e))
        } finally {
            setIsLoading(false)
        }
    }

    // No one found
    const renderStepNoUserFound = () => (
        <Box sx={{ py: 2 }}>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                    <strong>{t('invite_user_new_user_email')}</strong> {email || 'user@example.com'}
                </Typography>
            </Paper>

            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                {t('invite_user_enter_names')}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label={t('firstname')}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label={t('lastname')}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        variant="outlined"
                    />
                </Grid>
            </Grid>

            <DialogActions sx={{ px: 0, pt: 3 }}>
                <Button onClick={() => setCurrentStep(1)} variant="outlined" color="inherit">
                    {t('previous')}
                </Button>
                <LoadingButton
                    sx={{ background: colorPrimary(), color: "#fff" }}
                    loading={isLoading}
                    onClick={() => invitePerson(personSearch)}
                    disabled={!firstName.trim() || !lastName.trim()}
                    variant="contained"
                    color="primary"
                >
                    {t('invite_user_send_invitation')}
                </LoadingButton>
            </DialogActions>
        </Box>
    );

    // Some one found
    const renderStepUserFound = () => (
        <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                {t('invite_user_review_name')}
            </Typography>

            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('invite_user_new_user_email')}</strong> {email || personSearch?.email}
                </Typography>
                <Typography variant="body2">
                    <strong>{t('invite_user_new_user_name')}</strong> {personSearch?.firstname} {personSearch?.lastname}
                </Typography>
            </Paper>

            <DialogActions sx={{ px: 0, pt: 3 }}>
                <Button onClick={() => setCurrentStep(1)} variant="outlined" color="inherit">
                    {t('previous')}
                </Button>
                <LoadingButton
                    style={{ backgroundColor: colorPrimary(), color: "#ffffff" }}
                    loading={isLoading}
                    onClick={() => invitePerson(personSearch)}
                    variant="contained" color="primary">
                    {t('invite_user_send_invitation')}
                </LoadingButton>
            </DialogActions>
        </Box>
    );

    return (
        <>
            <Button
                style={{ backgroundColor: colorPrimary(), color: colorLight() }}
                variant='contained'
                onClick={() => setIsOpen(true)}>
                {t('add_user')}
            </Button>

            <Dialog
                open={isOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}>
                {/* Dialog */}
                <DialogTitle sx={{
                    bgcolor: colorPrimary(),
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 2
                }}>
                    <Typography variant="h6">
                        {t('invite_user_dialog_title')}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 3, pb: 2 }}>
                    {currentStep === 1 && renderStepInit()}
                    {currentStep === 2 && !searchSuccess && renderStepNoUserFound()}
                    {currentStep === 2 && searchSuccess && renderStepUserFound()}
                </DialogContent>
            </Dialog>
        </>

    );
};

export default UserInvitationDialog;